import type { PropertyFilterOperatorForm } from '@cloudscape-design/collection-hooks';
import type { DateRangePickerProps } from '@risk-smart/themed-cloudscape-components/date-range-picker';
import FormField from '@risk-smart/themed-cloudscape-components/form-field';
import SegmentedControl from '@risk-smart/themed-cloudscape-components/segmented-control';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePickerInput } from 'src/components/form/controlled-date-picker/ControlledDatePicker';
import { DateRangePickerInput } from 'src/components/form/controlled-date-range-picker/ControlledDateRangePicker';

import { defaultRelativeOptions } from '../form/controlled-date-range-picker/defaultRelativeOptions';
import type { DateFilterValue, DateThresholdMode } from './dateFilterValue';
import { isDateThresholdValue } from './dateFilterValue';

type Mode = 'range' | DateThresholdMode;

const modeOf = (value: DateFilterValue | null): Mode =>
  isDateThresholdValue(value) ? value.mode : 'range';

/**
 * Value form for the single `=` date operator (CUS-217).
 *
 * The inclusive one-sided comparisons live here rather than as separate `>=` /
 * `<=` operators: Cloudscape only auto-inserts an operator when a property has
 * exactly one, so adding operators would put an extra click in front of every
 * date filter and hide the range picker behind selecting "Equals".
 *
 * Range stays the default, so opening a date filter behaves exactly as it did
 * before this form existed.
 */
export const DateFilterForm: PropertyFilterOperatorForm<null | string> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation('common', { keyPrefix: 'dateFilter' });

  // Cloudscape types the token value as the operator's TokenValue, but a custom
  // form may carry any shape — the range operator has always asserted through
  // `unknown` for the same reason.
  const current = value as unknown as DateFilterValue | null;
  const emit = onChange as unknown as (value: DateFilterValue | null) => void;

  const [mode, setMode] = useState<Mode>(() => modeOf(current));

  const thresholdDate = isDateThresholdValue(current) ? current.date : '';
  const rangeValue = isDateThresholdValue(current)
    ? null
    : ((current as DateRangePickerProps.Value | null) ?? null);

  const onModeChange = (next: Mode) => {
    setMode(next);
    // Clear rather than convert: a range has two bounds and a threshold one, so
    // any mapping between them would silently invent or discard a bound.
    emit(null);
  };

  return (
    <div className={'date-time-form'} data-testid={'date-filter-form'}>
      <SpaceBetween direction={'vertical'} size={'m'}>
        <FormField label={t('modeLabel')}>
          <SegmentedControl
            selectedId={mode}
            onChange={(e) => onModeChange(e.detail.selectedId as Mode)}
            label={t('modeLabel')}
            options={[
              { id: 'range', text: t('mode.range') },
              { id: 'onOrAfter', text: t('mode.onOrAfter') },
              { id: 'onOrBefore', text: t('mode.onOrBefore') },
            ]}
          />
        </FormField>

        {mode === 'range' ? (
          <DateRangePickerInput
            value={rangeValue}
            onChange={(next) => emit(next)}
            placeholder={t('datePlaceholder')}
            relativeOptions={defaultRelativeOptions}
            isExpandable={true}
          />
        ) : (
          <FormField label={t(`mode.${mode}`)}>
            <div data-testid={'date-filter-threshold-input'}>
              <DatePickerInput
                value={thresholdDate}
                placeholder={t('datePlaceholder')}
                expandToViewport={true}
                onChange={(next) =>
                  // DatePickerInput emits a full ISO string built from a
                  // date-only value, so the date part is taken by splitting
                  // rather than reformatting — `new Date('YYYY-MM-DD')` is UTC
                  // midnight, and reformatting it in a negative-offset timezone
                  // would land on the previous day.
                  emit(
                    next
                      ? { type: 'threshold', mode, date: next.split('T')[0] }
                      : null
                  )
                }
              />
            </div>
          </FormField>
        )}
      </SpaceBetween>
    </div>
  );
};
